/** Epoch values at or above this are treated as milliseconds (below: seconds). */
const EPOCH_MS_THRESHOLD = 1e12;
/** Epoch values at or above this are treated as microseconds. */
const EPOCH_US_THRESHOLD = 1e15;

const HAS_TIMEZONE_SUFFIX_RE = /[zZ]$|[+-]\d{2}:?\d{2}$/;

function toValidDate(ms: number): Date | null {
  const d = new Date(ms);
  return isNaN(d.getTime()) ? null : d;
}

/** Normalize epoch seconds, milliseconds, or microseconds to milliseconds. */
export function epochMillisFromNumeric(n: number): number | null {
  if (!Number.isFinite(n)) return null;
  const abs = Math.abs(n);
  if (abs >= EPOCH_US_THRESHOLD) return Math.trunc(n / 1000);
  if (abs >= EPOCH_MS_THRESHOLD) return Math.trunc(n);
  return Math.trunc(n * 1000);
}

function parseIsoAsUtc(s: string): Date | null {
  const trimmed = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return toValidDate(new Date(`${trimmed}T00:00:00.000Z`).getTime());
  }
  const iso = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
  const normalized = HAS_TIMEZONE_SUFFIX_RE.test(iso)
    ? iso.replace(/z$/, "Z")
    : `${iso}Z`;
  return toValidDate(new Date(normalized).getTime());
}

/**
 * Parse backend UTC datetime wire values.
 * Supports epoch µs/ms/s, numeric strings, ISO with/without Z, and date-only.
 */
export function parseUtcDateTime(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  if (typeof value === "number") {
    const ms = epochMillisFromNumeric(value);
    return ms == null ? null : toValidDate(ms);
  }

  if (typeof value === "string") {
    const s = value.trim();
    if (/^\d+$/.test(s)) {
      const ms = epochMillisFromNumeric(Number(s));
      return ms == null ? null : toValidDate(ms);
    }
    return parseIsoAsUtc(s);
  }

  return null;
}

export function formatUtcDateTime(
  value: unknown,
  locale = "en-GB"
): string {
  const d = parseUtcDateTime(value);
  return d ? d.toLocaleString(locale) : String(value ?? "-");
}
